import { Alert, Badge, Button, Code, Group, Paper, Stack, Switch, Text, Title } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from 'mantine-datatable';
import { apiBaseUrl, rawApi } from '../api/client';
import { enumColor, enumLabel } from '../lib/enums';
import { formatDateTime, formatRelative } from '../lib/format';
import { notifyDone, notifyError } from '../lib/notify';

interface McpSettings {
  enabled: boolean;
  toolConfig: Record<string, { enabled?: boolean; autoApply?: boolean }>;
  ipAllowlist: string[] | null;
}

/** One change the server will actually honour an "apply without asking" setting for. */
interface AutoApplyChoice {
  toolName: string;
  label: string;
  description: string;
  autoApply: boolean;
}

const SETTINGS_KEY = ['mcp-settings'];
const CHOICES_KEY = ['mcp-auto-apply'];

/**
 * The sentence the assistant showed the owner when it asked.
 *
 * Every propose_* tool builds one deliberately — real money, real names, no identifiers — and it
 * is stored alongside the raw payload. This screen used to print `JSON.stringify(...)` cut off at
 * 120 characters, so the one readable thing in the record was the one thing not shown.
 */
function summaryOf(diffPreview: unknown): string | null {
  const preview = diffPreview as { summary?: unknown } | null;
  return typeof preview?.summary === 'string' && preview.summary.trim() ? preview.summary : null;
}

function warningsOf(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((w): w is string => typeof w === 'string') : [];
}

/**
 * What became of a request, rather than the word stored against it.
 *
 * A request is only good for a quarter of an hour, and nothing sweeps the table afterwards — the
 * row is only marked as having run out when somebody tries to apply it, which for an abandoned one
 * never happens. So a suggestion the owner simply moved on from sat here in amber "Not confirmed"
 * for months, on a screen whose own words say there is nothing here to act on.
 */
function effectiveStatus(row: Record<string, unknown>): string {
  const status = String(row.status ?? '');
  if (status !== 'pending') return status;
  const expiresAt = typeof row.expiresAt === 'string' ? row.expiresAt : null;
  return expiresAt && expiresAt < new Date().toISOString() ? 'expired' : status;
}

/**
 * Which assistant this was, out of the line the client sent about itself.
 *
 * That line is a string of version numbers and platform names — the sort of thing a browser tells
 * a web server. The product at the front of it is the only part that answers the question the
 * column is asking, and the whole line stays on hover for whoever is diagnosing a connection.
 */
function clientLabel(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const first = value.trim().split(/\s+/)[0] ?? '';
  return (first.split('/')[0] || first).slice(0, 40) || null;
}

/**
 * What was asked, and what the assistant flagged about it.
 *
 * The warnings ("this leaves nobody to call at Gibson Bight", "that vendor does not normally bill
 * for shipping") were written, stored and then never shown anywhere — so the record of a change
 * that went through despite a caution read exactly like one that had none.
 */
function AskedCell({ row }: { row: Record<string, unknown> }) {
  const summary = summaryOf(row.diffPreview);
  const warnings = warningsOf(row.validationWarnings);
  return (
    <Stack gap={2}>
      <Text size="sm">{summary ?? 'No description was recorded.'}</Text>
      {warnings.map((w) => (
        <Text key={w} size="xs" c="orange.8">
          {w}
        </Text>
      ))}
    </Stack>
  );
}

/**
 * The house rule for one kind of change: stop and ask, or go ahead.
 *
 * Saved by merging into the stored settings rather than replacing them, so turning one switch
 * cannot quietly drop a setting some other part of the system put there.
 */
function AutoApplyRow({ choice, settings }: { choice: AutoApplyChoice; settings?: McpSettings }) {
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: (autoApply: boolean) => {
      if (!settings) throw new Error('The current settings have not loaded yet.');
      const existing = settings.toolConfig[choice.toolName] ?? {};
      return rawApi('PUT', '/admin/v1/mcp-admin/settings', {
        ...settings,
        toolConfig: { ...settings.toolConfig, [choice.toolName]: { ...existing, autoApply } },
      });
    },
    onSuccess: (_data, autoApply) => {
      void queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
      void queryClient.invalidateQueries({ queryKey: CHOICES_KEY });
      notifyDone(
        autoApply ? 'Set to go ahead' : 'Set to ask first',
        autoApply
          ? `An assistant can now do this without checking with you: ${choice.label.toLowerCase()}.`
          : `An assistant will ask you before it does this: ${choice.label.toLowerCase()}.`,
      );
    },
    onError: (err) => notifyError(err, `change the rule for "${choice.label.toLowerCase()}"`),
  });

  return (
    <Group justify="space-between" wrap="nowrap" align="flex-start" gap="md">
      <div>
        <Text size="sm" fw={500}>
          {choice.label}
        </Text>
        <Text size="xs" c="dimmed">
          {choice.description}
        </Text>
      </div>
      <Switch
        aria-label={choice.label}
        checked={choice.autoApply}
        disabled={!settings || save.isPending}
        onChange={(e) => save.mutate(e.currentTarget.checked)}
        onLabel="GO"
        offLabel="ASK"
        size="md"
        style={{ flexShrink: 0 }}
      />
    </Group>
  );
}

export function McpPage() {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => rawApi<McpSettings>('GET', '/admin/v1/mcp-admin/settings'),
  });
  const choices = useQuery({
    queryKey: CHOICES_KEY,
    queryFn: () => rawApi<AutoApplyChoice[]>('GET', '/admin/v1/mcp-admin/auto-apply-tools'),
  });
  const proposals = useQuery({
    queryKey: ['mcp-proposals'],
    queryFn: () => rawApi<Record<string, unknown>[]>('GET', '/admin/v1/mcp-admin/proposals?limit=100'),
  });
  const sessions = useQuery({
    queryKey: ['mcp-sessions'],
    queryFn: () => rawApi<Record<string, unknown>[]>('GET', '/admin/v1/mcp-admin/sessions'),
  });

  const killSwitch = useMutation({
    // The endpoint REPLACES the settings row: an absent `toolConfig` is stored as `{}` and an
    // absent `ipAllowlist` as null. So the current settings have to be in hand before this switch
    // does anything — flipping it against a read that failed would wipe the address allowlist and
    // every "go ahead" rule below, and show a green notice while doing it.
    mutationFn: (enabled: boolean) => {
      if (!settings.data) throw new Error('The current settings have not loaded yet.');
      return rawApi('PUT', '/admin/v1/mcp-admin/settings', { ...settings.data, enabled });
    },
    onSuccess: (_data, enabled) => {
      void queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
      notifyDone(
        enabled ? 'Assistants can connect' : 'Assistants are shut out',
        enabled
          ? 'An assistant you have named can now reach this bar’s records.'
          : 'Every assistant is refused straight away, whoever they are.',
      );
    },
    onError: (err) => notifyError(err, 'change whether assistants can connect'),
  });

  const allowlist = settings.data?.ipAllowlist ?? [];

  return (
    <>
      <Title order={3} mb="md">
        Assistant access
      </Title>
      <Alert color="grape" mb="md" title="Letting an assistant work on your behalf">
        An AI assistant can be connected to this bar's records at{' '}
        <Code>{apiBaseUrl || window.location.origin}/mcp</Code>. Only people you have named can connect, and
        everything an assistant does is written to the activity log alongside anything your staff do, so you
        can always see who changed what.
      </Alert>

      <Paper withBorder p="md" radius="md" mb="md">
        {/* A switch that cannot read its own setting still draws itself, and it draws itself OFF.
            So a failed read looked exactly like assistants being shut out — the owner walks away
            believing nobody can reach the bar's records while the endpoint is wide open. Say the
            read failed instead, and show nothing that could be mistaken for an answer. */}
        {settings.isError ? (
          <Alert color="red" title="Could not tell whether assistants are allowed">
            <Text size="sm" mb="sm">
              Nothing has changed. Until this loads, this screen cannot say whether assistants can connect or
              not — do not read anything into it either way.
            </Text>
            <Button size="xs" variant="light" onClick={() => void settings.refetch()}>
              Try again
            </Button>
          </Alert>
        ) : (
          <Group justify="space-between">
            <div>
              <Text fw={600}>Allow assistants to connect</Text>
              <Text size="xs" c="dimmed">
                Turn this off and every assistant is refused straight away, no matter who they are.
              </Text>
            </div>
            <Switch
              size="lg"
              onLabel="ON"
              offLabel="OFF"
              checked={settings.data?.enabled ?? false}
              disabled={!settings.data || killSwitch.isPending}
              onChange={(e) => killSwitch.mutate(e.currentTarget.checked)}
            />
          </Group>
        )}
        {/* A connection refused by the allowlist looks identical to a broken assistant from the
            outside. Saying the list exists is the difference between a two-minute fix and an
            afternoon. The addresses themselves are set up once, with help. */}
        {allowlist.length > 0 && (
          <Text size="xs" c="dimmed" mt="sm">
            Connections are also limited to {allowlist.length} known internet address
            {allowlist.length === 1 ? '' : 'es'}. An assistant connecting from anywhere else is refused even
            when the switch above is on — ask for help if that needs to change.
          </Text>
        )}
      </Paper>

      <Paper withBorder p="md" radius="md" mb="md">
        <Text fw={600} mb={4}>
          What an assistant may do without asking
        </Text>
        <Text size="xs" c="dimmed" mb="md">
          An assistant asks you to confirm every change before it happens. These few are safe enough that you
          can let them through on their own — none of them touches a price, money, pay, stock quantities or
          anything a guest is charged, and none of them deletes.
        </Text>
        {choices.isError ? (
          <Alert color="red" title="Could not load these rules">
            <Text size="sm">Nothing has changed — this is only a display problem.</Text>
          </Alert>
        ) : (
          <Stack gap="md">
            {(choices.data ?? []).map((choice) => (
              <AutoApplyRow key={choice.toolName} choice={choice} settings={settings.data} />
            ))}
          </Stack>
        )}
        <Text size="xs" c="dimmed" mt="md">
          Everything else always stops and asks you first — prices, invoices, payments, pay, stock counts,
          staff and anything a register can do. That cannot be switched off from here.
        </Text>
      </Paper>

      <Paper withBorder p="md" radius="md" mb="md">
        <Text fw={600} mb={4}>
          What an assistant has asked to change
        </Text>
        {/*
          Says plainly what this table is, because the table alone looks like a queue you act on.
          There is no approve/decline route behind this screen: the assistant asks in the
          conversation, you answer there, and it applies the change itself. This is the record.
        */}
        <Text size="xs" c="dimmed" mb="sm">
          An assistant asks you to confirm each change in the conversation you are having with it, and applies
          it there once you say yes. This is the record of what was asked and what happened — there is nothing
          to approve or turn down here.
        </Text>
        <DataTable
          idAccessor="id"
          records={proposals.data ?? []}
          fetching={proposals.isLoading}
          minHeight={proposals.data?.length ? undefined : 100}
          noRecordsText="No assistant has suggested a change yet"
          verticalSpacing="sm"
          columns={[
            {
              accessor: 'createdAt',
              title: 'When',
              noWrap: true,
              render: (r) => formatDateTime(r.createdAt as string),
            },
            { accessor: 'diffPreview', title: 'What it asked to do', render: (r) => <AskedCell row={r} /> },
            {
              accessor: 'status',
              title: 'What happened',
              noWrap: true,
              render: (r) => {
                const status = effectiveStatus(r);
                return (
                  <Badge color={enumColor('proposalStatus', status) ?? 'gray'} variant="light">
                    {enumLabel('proposalStatus', status)}
                  </Badge>
                );
              },
            },
            {
              // The name captured when the change was confirmed. `confirmedBy` alongside it is an
              // Entra object id and is never rendered — nothing resolves it back to a person.
              accessor: 'confirmedByName',
              title: 'Approved by',
              render: (r) => (r.confirmedByName ? (r.confirmedByName as string) : <Text c="dimmed">—</Text>),
            },
          ]}
        />
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Text fw={600} mb="sm">
          Who has connected
        </Text>
        <DataTable
          idAccessor="id"
          records={sessions.data ?? []}
          fetching={sessions.isLoading}
          minHeight={sessions.data?.length ? undefined : 100}
          noRecordsText="Nobody has connected an assistant yet"
          columns={[
            { accessor: 'userName', title: 'Person' },
            {
              accessor: 'clientName',
              title: 'Using',
              render: (r) => {
                const label = clientLabel(r.clientName);
                return label ? (
                  <Text size="sm" title={String(r.clientName)}>
                    {label}
                  </Text>
                ) : (
                  <Text c="dimmed">Not recorded</Text>
                );
              },
            },
            {
              accessor: 'startedAt',
              title: 'Started',
              noWrap: true,
              render: (r) => formatDateTime(r.startedAt as string),
            },
            {
              // "Last seen 4 minutes ago" answers the question this table exists for — is somebody
              // connected right now — which a bare timestamp leaves you working out by hand.
              accessor: 'lastSeenAt',
              title: 'Last seen',
              noWrap: true,
              render: (r) =>
                r.lastSeenAt ? (
                  <Text size="sm">{formatRelative(r.lastSeenAt as string)}</Text>
                ) : (
                  <Text c="dimmed">—</Text>
                ),
            },
          ]}
        />
      </Paper>
    </>
  );
}
